import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import MuiAccordion from "@mui/material/Accordion"
import MuiAccordionSummary from "@mui/material/AccordionSummary"
import MuiAccordionDetails from "@mui/material/AccordionDetails"
import { Link } from "utils"
import { richTextListStyles } from "../../common/richTextListStyles"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"
import { typographyVariants } from "../../theme/typography"
import {
  HEADER_HEIGHT_MOBILE,
  HEADER_HEIGHT_DESKTOP,
} from "../../layout/Header/styles"

/* ------------------------------------------------------------------ */
/* Root — full-width section with vertical padding                     */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  padding: ${sizes.spacing[48]}px 0;
  /* EGDE-362: paging scrolls back to the section top, below the fixed header. */
  scroll-margin-top: ${HEADER_HEIGHT_MOBILE}px;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
    gap: ${sizes.spacing[48]}px;
    scroll-margin-top: ${HEADER_HEIGHT_DESKTOP}px;
  }
`

/* ------------------------------------------------------------------ */
/* Title row — chevron icon + title text                                */
/* ------------------------------------------------------------------ */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  justify-content: flex-start;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[6]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Title text                                                          */
/* ------------------------------------------------------------------ */
export const Title = styled(Typography)`
  color: ${colors.light.text.primary};
`

/* ------------------------------------------------------------------ */
/* Chevron wrapper — next to title                                     */
/* ------------------------------------------------------------------ */
export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

/* ------------------------------------------------------------------ */
/* FAQ List — column of accordion items                                */
/* ------------------------------------------------------------------ */
export const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: 1200px;
  }
`

/* ------------------------------------------------------------------ */
/* Accordion — single FAQ item, fully unstyled MUI                     */
/* ------------------------------------------------------------------ */
export const StyledAccordion = styled(MuiAccordion)`
  width: 100%;
  box-shadow: none;
  background-color: transparent;
  border-radius: 0 !important;
  border-top: 1px solid ${colors.primitives.neutral300};

  &:last-of-type {
    border-bottom: 1px solid ${colors.primitives.neutral300};
  }

  &::before {
    display: none;
  }

  &.Mui-expanded {
    margin: 0;
  }
`

/* ------------------------------------------------------------------ */
/* Accordion Summary — question row with plus/minus icon               */
/* ------------------------------------------------------------------ */
export const StyledAccordionSummary = styled(MuiAccordionSummary)`
  padding: ${sizes.spacing[16]}px 0;
  /* EGDE-361: mobile cells must match the 84px design height. */
  min-height: 84px;
  gap: ${sizes.spacing[16]}px;
  cursor: pointer;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[24]}px 0;
    min-height: unset;

    /* EGDE-506 r2: when open, the answer hugs the question — the design gap
       is ~20px, carried here rather than on the details padding. */
    &.Mui-expanded {
      padding-block-end: ${sizes.spacing[16]}px;
    }
  }

  & .MuiAccordionSummary-content {
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${sizes.spacing[8]}px;
    justify-content: flex-start;

    &.Mui-expanded {
      margin: 0;
    }
  }

  & .MuiAccordionSummary-expandIconWrapper {
    transform: none;
    flex-shrink: 0;

    &.Mui-expanded {
      transform: none;
    }
  }
`

/* ------------------------------------------------------------------ */
/* Question text                                                       */
/* ------------------------------------------------------------------ */
export const QuestionText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$expanded",
})<{ $expanded?: boolean }>`
  color: ${colors.primitives.neutral600};
  font-weight: ${(p) => (p.$expanded ? 700 : 500)} !important;
  text-align: start;

  @media (min-width: ${breakpoints.lg}px) {
    ${StyledAccordionSummary}:hover & {
      font-weight: 700 !important;
    }
  }
`

/* ------------------------------------------------------------------ */
/* Accordion Details — answer text area                                */
/* ------------------------------------------------------------------ */
export const StyledAccordionDetails = styled(MuiAccordionDetails)`
  /* EGDE-506 r2: the answer sits close under its question per the design —
     the tightened gap lives in the expanded summary's bottom padding. */
  padding: 0 0 ${sizes.spacing[16]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: 0 0 ${sizes.spacing[24]}px 0;
  }
`

/* ------------------------------------------------------------------ */
/* Answer text                                                         */
/* ------------------------------------------------------------------ */
export const AnswerText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: start;
`

/* EGDE-360: when the CMS answer is a Rich Text Editor value, render its markup
   so editors can embed links and icons. Plain-string answers keep using
   AnswerText above. */
export const AnswerRichText = styled.div`
  color: ${colors.primitives.neutral600};
  text-align: start;
  /* EGDE-736: the rich-text answer must read at the same size as the
     plain-string answer above (AnswerText, variant body2) — 16px on
     mobile, 18px from lg up. It was pinned to 16px at every width. */
  font-size: ${typographyVariants.body2.mobile.fontSize}px;
  /* The 1.5 leading is the rich-text renderer's own and predates this ticket —
     body2's tighter token pair is measured for one-line labels, and adopting it
     took mobile answers from 24px to 20px of leading, which reads cramped on
     exactly the multi-paragraph answers QA is looking at. Size only. */
  line-height: 1.5;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: ${typographyVariants.body2.desktop.fontSize}px;
  }

  & > *:first-of-type {
    margin-top: 0;
  }
  & > *:last-child {
    margin-bottom: 0;
  }
  & p {
    margin: 0 0 ${sizes.spacing[8]}px;
  }
  & a {
    /* EGDE-395: FAQ answer hyperlinks render blue, not green/orange.
       !important overrides the inline color injected by replaceRichTextLinks. */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }
  /* EGDE-330: brand-chevron bullets, shared with every rich-text renderer. */
  ${richTextListStyles}
  & img {
    display: inline-block;
    vertical-align: middle;
    max-width: 100%;
    height: auto;
  }
`

/* ------------------------------------------------------------------ */
/* CTA Button — outline style with orange border                       */
/* ------------------------------------------------------------------ */
export const CTAButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  border: 2px solid ${colors.primitives.secondary500};
  border-radius: ${sizes.borderRadius.full}px;
  background-color: ${colors.light.bg.primary};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  align-self: flex-start;

  /* EGDE-500: Main CTA sheet — the secondary (outlined) pill hovers to the
     CTA gradient and presses to the solid orange; the swap is instant
     (cf. EGDE-355) and the border melts into the fill. */
  &:hover,
  &:focus-visible {
    background: ${colors.gradients.gradientCta};
    border-color: transparent;
  }

  &:active {
    background: ${colors.primitives.primary500};
    border-color: transparent;
  }
`

/* ------------------------------------------------------------------ */
/* CTA Anchor — same style for Media/External links (ESEIM-173)        */
/* ------------------------------------------------------------------ */
export const CTAAnchor = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  border: 2px solid ${colors.primitives.secondary500};
  border-radius: ${sizes.borderRadius.full}px;
  background-color: ${colors.light.bg.primary};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  align-self: flex-start;

  /* EGDE-500: same secondary hover/press as CTAButton above. */
  &:hover,
  &:focus-visible {
    background: ${colors.gradients.gradientCta};
    border-color: transparent;
  }

  &:active {
    background: ${colors.primitives.primary500};
    border-color: transparent;
  }
`

/* ------------------------------------------------------------------ */
/* CTA Button text                                                     */
/* ------------------------------------------------------------------ */
export const CTAText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  white-space: nowrap;
`

/* ------------------------------------------------------------------ */
/* Plus/Minus icon wrapper                                             */
/* ------------------------------------------------------------------ */
export const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* EGDE-506 r2: the FAQ +/− toggle is gray in the design (#757575 sampled →
     neutral500), unlike the orange side-menu toggles. */
  color: ${colors.primitives.neutral500};
`

/* ------------------------------------------------------------------ */
/* Pagination (EGDE-362) — mirrors the inner FAQ page paginator        */
/* ------------------------------------------------------------------ */
export const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
  align-self: center;
`

export const PaginationArrow = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: ${colors.primitives.neutral600};
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  /* EGDE-496: the glyph is already black at rest, so hover paints the
     neutral200 disc behind it instead (neutral100 is indistinguishable from
     white). */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral200};
  }
`

export const PaginationDot = styled.button<{ $active: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid
    ${(p) =>
      p.$active
        ? colors.primitives.warning500
        : colors.primitives.neutral300};
  background: ${(p) =>
    p.$active ? colors.primitives.secondary500 : colors.primitives.neutral0};
  color: ${colors.primitives.neutral600};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  transition: background 0.2s ease, border-color 0.2s ease;

  /* EGDE-496: same subtle ring-darkening hover as the lobby page circles. */
  &:hover:not(:disabled) {
    border-color: ${colors.primitives.neutral500};
  }
`
